"use client";

import { useRef, useEffect, useState } from "react";
import {
  ClipboardList,
  Calendar,
  FileEdit,
  CheckCircle,
  CircleOff,
  XCircle,
  PhoneOff,
  Trash2,
  FileText,
  Clock,
  Star,
  Heart,
  Zap,
  Target,
  Flag,
  Bookmark,
  Award,
  TrendingUp,
  Users,
  UserCheck,
  Phone,
  Mail,
  MessageSquare,
  DollarSign,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import type { PipelineStage } from "../types/pipeline-stage.types";

interface StatusCardsProps {
  selectedStatus?: string;
  onStatusSelect?: (status: string | undefined) => void;
  statusCounts: Record<string, number | { count: number; displayName: string }>;
  disableClick?: boolean;
  pipelineStages?: PipelineStage[];
}

// Icon lookup for pipeline stages (by icon name stored in DB)
const ICON_MAP: Record<string, LucideIcon> = {
  ClipboardList,
  Calendar,
  FileEdit,
  CheckCircle,
  CircleOff,
  XCircle,
  PhoneOff,
  Trash2,
  FileText,
  Clock,
  Star,
  Heart,
  Zap,
  Target,
  Flag,
  Bookmark,
  Award,
  TrendingUp,
  Users,
  UserCheck,
  Phone,
  Mail,
  MessageSquare,
  DollarSign,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
};

// Fallback color configs for known system statuses (used when pipeline stages not loaded yet)
const SYSTEM_STATUS_COLORS: Record<string, string> = {
  'new lead': '#3b82f6',
  'leads': '#3b82f6',
  'appt': '#0891b2',
  'appointment': '#0891b2',
  'underwriting': '#f59e0b',
  'sold & approved': '#059669',
  'sold': '#059669',
  'bad number': '#ef4444',
  'no sale': '#dc2626',
  'do not call': '#92400e',
  'disposed': '#6b7280',
  'declined': '#6b7280',
  'lapsed': '#111827',
};

export const StatusCards = function({
  selectedStatus,
  onStatusSelect,
  statusCounts,
  disableClick = false,
  pipelineStages,
}: StatusCardsProps) {
  const handleStatusClick = (status: string, e?: React.MouseEvent) => {
    // Blur the button immediately so the browser repaints the selected style
    (e?.currentTarget as HTMLElement)?.blur();
    if (selectedStatus === status) {
      onStatusSelect?.(undefined);
    } else {
      onStatusSelect?.(status);
    }
  };

  const getCount = (statusData: number | { count: number; displayName: string }): number => {
    if (typeof statusData === 'number') return statusData;
    if (statusData && typeof statusData === 'object' && 'count' in statusData) return statusData.count;
    return 0;
  };

  // Build status cards from pipeline stages (dynamic) or fallback to statusCounts
  const buildCards = () => {
    if (pipelineStages && pipelineStages.length > 0) {
      // Dynamic mode: use pipeline stages as the source of truth
      return pipelineStages
        .filter(stage => stage.name.toLowerCase() !== 'disposed')
        .map(stage => {
          // Find matching count from statusCounts using systemValue or name
          const matchKey = stage.systemValue || stage.name;
          const countData = statusCounts[matchKey] || statusCounts[stage.name] || 0;
          const count = getCount(countData);

          // Display name: for the "New lead" system stage, show "Leads"
          let displayName = stage.name;
          if (stage.systemValue === 'New lead') displayName = 'Leads';

          const IconComponent = stage.icon ? (ICON_MAP[stage.icon] || FileText) : FileText;

          return {
            status: matchKey,
            displayName,
            count,
            color: stage.color,
            icon: IconComponent,
            stageId: stage.id,
          };
        });
    }

    // Fallback mode: build from statusCounts (backward compatible)
    const consolidatedStatuses: Record<string, { status: string; displayName: string; count: number }> = {};

    Object.entries(statusCounts || {}).forEach(([status, statusData]) => {
      const count = getCount(statusData);
      const statusLower = status.toLowerCase();
      let displayName = status;
      if (statusLower === 'new lead' || statusLower === 'new') displayName = 'Leads';
      else if (statusData && typeof statusData === 'object' && 'displayName' in statusData) {
        displayName = statusData.displayName;
      }

      if (consolidatedStatuses[statusLower]) {
        consolidatedStatuses[statusLower].count += count;
      } else {
        consolidatedStatuses[statusLower] = { status, displayName, count };
      }
    });

    const preferredOrder = ['new lead', 'leads', 'appt', 'appointment', 'underwriting', 'sold & approved', 'sold', 'bad number', 'no sale', 'do not call', 'declined', 'lapsed'];

    return Object.values(consolidatedStatuses)
      .filter(s => s.status.toLowerCase() !== 'disposed')
      .map(s => ({
        status: s.status,
        displayName: s.displayName,
        count: s.count,
        color: SYSTEM_STATUS_COLORS[s.status.toLowerCase()] || '#6b7280',
        icon: ICON_MAP[s.status] || FileText,
        stageId: undefined as string | undefined,
      }))
      .sort((a, b) => {
        const aIdx = preferredOrder.indexOf(a.status.toLowerCase());
        const bIdx = preferredOrder.indexOf(b.status.toLowerCase());
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
  };

  const statusCards = buildCards();

  if (statusCards.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No lead statuses found</p>
      </div>
    );
  }

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  // Shift+wheel scrolls the row horizontally + track scroll state.
  //
  // This used to convert ANY vertical wheel into horizontal movement while the pointer was
  // over the row. Two problems, both reported from the floor as the stages "slowly sliding"
  // and ruining click aim:
  //   1. a plain vertical scroll over the row moved it sideways instead of scrolling the page
  //   2. trackpad momentum keeps emitting decaying deltaY for ~a second AFTER the fingers
  //      lift, so the row drifted while the user was already reaching for a card
  // There is no reliable way to tell a momentum event from a real one, so the fix is to stop
  // treating vertical intent as horizontal at all. Shift+wheel is the platform convention,
  // native two-finger horizontal still works, and mouse users have the hover arrows and the
  // arrow keys below.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return; // let the page scroll; never steal vertical intent
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // already horizontal, browser handles it
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [statusCards.length]);

  // Arrow key navigation while hovering the stages row
  useEffect(() => {
    if (!isHovering) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack when typing in an input/textarea/contentEditable
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByAmount('right');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByAmount('left');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isHovering]);

  const scrollByAmount = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
    // Force re-check after the smooth scroll animation finishes
    setTimeout(updateScrollState, 350);
  };

  return (
    <div
      className="relative group"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Hover-activated scroll arrows (visible only on hover when overflow exists) */}
      {isHovering && (canScrollLeft || canScrollRight) && (
        <>
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollByAmount('left')}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-30 h-9 w-9 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition pointer-events-auto"
          >
            <ChevronLeft className="h-5 w-5 text-gray-700 pointer-events-none" />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollByAmount('right')}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-30 h-9 w-9 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition pointer-events-auto"
          >
            <ChevronRight className="h-5 w-5 text-gray-700 pointer-events-none" />
          </button>
        </>
      )}
      {/* Force always-visible horizontal scrollbar on every OS/browser,
          including Mac (which hides it by default in System Preferences) */}
      <style jsx>{`
        .status-cards-scroll::-webkit-scrollbar {
          height: 10px;
          -webkit-appearance: none;
        }
        .status-cards-scroll::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 999px;
        }
        .status-cards-scroll::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 999px;
          border: 2px solid #f3f4f6;
        }
        .status-cards-scroll::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        .status-cards-scroll {
          scrollbar-width: thin;
          scrollbar-color: #9ca3af #f3f4f6;
        }
      `}</style>
      {/* Horizontally scrollable container. data-no-ptr: in the native shell
          this row lives inside PullToRefresh, whose touch tracking fights the
          horizontal pan and made finger-scrolling work only intermittently on
          iOS — the attribute tells it to keep its hands off gestures that
          start here (same fix as the carrier table / dial charts). */}
      <div
        ref={scrollRef}
        data-no-ptr
        className="status-cards-scroll flex gap-3 overflow-x-scroll pb-3"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {statusCards.map((card) => {
          const isSelected = selectedStatus === card.status;
          const IconComponent = card.icon;

          const content = (
            <div className="flex flex-col items-center justify-center text-center space-y-1.5 h-full">
              <div className="transition-transform duration-200">
                <IconComponent className="h-6 w-6" />
              </div>
              <div className="text-xs font-medium opacity-95 leading-tight text-center max-w-[100px]" title={card.displayName}>
                {card.displayName}
              </div>
              <div className="text-xl font-bold">({card.count})</div>
              {!!isSelected && (
                <div className="text-[10px] opacity-75 font-medium">SELECTED</div>
              )}
            </div>
          );

          const cardStyle: React.CSSProperties = {
            backgroundColor: card.color,
            border: isSelected ? '3px solid rgba(0,0,0,0.85)' : '3px solid transparent',
          };

          if (disableClick) {
            return (
              <div
                key={card.status}
                aria-label={`${card.displayName} status, ${card.count} items`}
                className="text-white p-4 rounded-xl shadow-sm transition-all duration-200 border-0 min-w-[140px] min-h-[100px] flex-1 flex items-center justify-center"
                style={cardStyle}
              >
                {content}
              </div>
            );
          }

          return (
            <button
              key={card.status}
              aria-label={`Filter by ${card.displayName} status, ${card.count} items`}
              aria-pressed={isSelected}
              onClick={(e) => handleStatusClick(card.status, e)}
              type="button"
              className="text-white p-4 rounded-xl cursor-pointer group hover:brightness-110 min-w-[140px] min-h-[100px] flex-1 flex items-center justify-center focus:outline-none focus:ring-0"
              style={cardStyle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStatusClick(card.status);
                }
              }}
            >
              {content}
            </button>
          );
        })}

      </div>
    </div>
  );
};
